/**
 * Module catalogue (SRS 5.5 — Module Licensing & Entitlements).
 *
 * The Owner sells modules individually or in any combination per tenant.
 * `CORE` is always-on: it holds identity, tenancy, RBAC, audit and patient MPI,
 * without which nothing else can function.
 */
export const MODULE_CODES = [
    'core',
    'opd',
    'ipd',
    'pharmacy',
    'billing',
    'lab',
    'radiology',
    'er',
    'ot',
    'blood_bank',
    'inventory',
    'hr',
    'accounting',
    'insurance',
    'portal',
    'mrd',
    'reports',
    'notifications',
    'ambulance',
    'dietary',
    'housekeeping',
    'feedback',
    'tasks',
    'maternity',
    'dialysis',
    'daycare',
    'dental',
    'nphies',
    'wasfaty',
];
/** Modules that can never be disabled — the platform would not function. */
export const ALWAYS_ON_MODULES = ['core'];
/**
 * Dependency graph (SRS 5.5 "Dependencies engine").
 * A module may only be enabled when every module it depends on is enabled,
 * and may not be disabled while another enabled module depends on it.
 */
export const MODULE_DEPENDENCIES = {
    core: [],
    opd: ['core'],
    ipd: ['core', 'billing'],
    pharmacy: ['core', 'inventory'],
    billing: ['core'],
    lab: ['core', 'billing'],
    radiology: ['core', 'billing'],
    er: ['core'],
    ot: ['core', 'ipd'],
    blood_bank: ['core'],
    inventory: ['core'],
    hr: ['core'],
    accounting: ['core', 'billing'],
    insurance: ['core', 'billing'],
    portal: ['core'],
    mrd: ['core'],
    reports: ['core'],
    notifications: ['core'],
    ambulance: ['core'],
    dietary: ['core'],
    housekeeping: ['core'],
    feedback: ['core'],
    tasks: ['core'],
    // A labour room is a ward: it admits, it charges bed days, it discharges.
    maternity: ['core', 'ipd'],
    dialysis: ['core', 'billing'],
    daycare: ['core', 'billing'],
    dental: ['core', 'opd'],
    nphies: ['core', 'insurance'],
    wasfaty: ['core', 'pharmacy'],
};
export const MODULE_CATALOGUE = [
    { code: 'core', name: 'Core Platform', group: 'core', description: 'Identity, tenancy, RBAC, audit, patient master index. Always enabled.' },
    { code: 'opd', name: 'Out-Patient (OPD)', group: 'clinical', description: 'Appointments, tokens, consultations, EMR, e-prescriptions.' },
    { code: 'ipd', name: 'In-Patient (IPD)', group: 'clinical', description: 'Admissions, bed board, CPOE, eMAR, discharge.' },
    { code: 'er', name: 'Emergency (ER)', group: 'clinical', description: 'Rapid registration, triage board, dispositions, MLC.' },
    { code: 'ot', name: 'Operation Theatre', group: 'clinical', description: 'OT scheduling, pre/intra/post-op records, implant capture.' },
    { code: 'blood_bank', name: 'Blood Bank', group: 'clinical', description: 'Donors, components, cross-match, issue and transfusion records.' },
    { code: 'pharmacy', name: 'Pharmacy', group: 'commerce', description: 'Retail POS, prescription queue, ward indents, batch/expiry stock.' },
    { code: 'inventory', name: 'Inventory & Procurement', group: 'commerce', description: 'Stores, stock movements, purchase orders, GRN, assets.' },
    { code: 'lab', name: 'Laboratory (LIS)', group: 'diagnostics', description: 'Test master, accession, results, verification and release.' },
    { code: 'radiology', name: 'Radiology (RIS)', group: 'diagnostics', description: 'Modality worklist, reporting, verification and release.' },
    { code: 'billing', name: 'Billing & Cashier', group: 'commerce', description: 'Charge master, tariffs, invoices, payments, refunds, shifts.' },
    { code: 'insurance', name: 'Insurance / Panel / TPA', group: 'commerce', description: 'Payers, coverage, pre-authorisation, claims lifecycle.' },
    { code: 'accounting', name: 'Accounting (GL)', group: 'commerce', description: 'Chart of accounts, journals, ledgers, doctor payouts.' },
    { code: 'hr', name: 'HR & Payroll', group: 'admin', description: 'Employees, rosters, attendance, leave, payroll.' },
    { code: 'mrd', name: 'Medical Records', group: 'admin', description: 'Record completion, ICD coding worklist, release workflow.' },
    { code: 'reports', name: 'Reports & Analytics', group: 'admin', description: 'Dashboards, standard report library, KPI engine.' },
    { code: 'notifications', name: 'Notifications', group: 'admin', description: 'SMS / WhatsApp / email / push templates and triggers.' },
    { code: 'ambulance', name: 'Ambulance & Transport', group: 'admin', description: 'Fleet, drivers, trip log and trip charges.' },
    { code: 'dietary', name: 'Dietary / Kitchen', group: 'admin', description: 'Diet orders, meal planning, delivery tick sheets.' },
    { code: 'housekeeping', name: 'Laundry & Housekeeping', group: 'admin', description: 'Linen issue/return, bed-cleaning workflow.' },
    { code: 'feedback', name: 'Feedback & Complaints', group: 'admin', description: 'Patient feedback, NPS, complaint ticketing.' },
    { code: 'tasks', name: 'Tasks & Messaging', group: 'admin', description: 'Task assignment to support staff, patient-context chat.' },
    { code: 'maternity', name: 'Labour & Delivery', group: 'clinical', description: 'Partograph, delivery notes, newborn record and birth register.' },
    { code: 'dialysis', name: 'Dialysis Unit', group: 'clinical', description: 'Machine allocation, session charting and per-session billing.' },
    { code: 'daycare', name: 'Day Care & Chemotherapy', group: 'clinical', description: 'Chair management, regimen cycles and physiotherapy packages.' },
    { code: 'dental', name: 'Dental', group: 'clinical', description: 'Tooth charting and multi-visit treatment plans.' },
    { code: 'portal', name: 'Patient Portal', group: 'patient', description: 'Patient self-service: appointments, reports, bills, payments.' },
    { code: 'nphies', name: 'NPHIES Exchange', group: 'commerce', description: 'Live eligibility checks, pre-authorisation and claims exchanged with insurers through NPHIES, sent from this facility\u2019s own internet connection.' },
    { code: 'wasfaty', name: 'Wasfaty E-Prescription', group: 'commerce', description: 'Download a patient Wasfaty prescription at the counter, have the payer authorise it, dispense it and claim it, sent from this facility\u2019s own internet connection.' },
];
/** All modules that must additionally be enabled for `code`, transitively. */
export function resolveModuleDependencies(code) {
    const seen = new Set();
    const walk = (c) => {
        for (const dep of MODULE_DEPENDENCIES[c] ?? []) {
            if (seen.has(dep))
                continue;
            seen.add(dep);
            walk(dep);
        }
    };
    walk(code);
    return [...seen];
}
/** Enabled modules that would break if `code` were disabled. */
export function resolveModuleDependents(code, enabled) {
    return enabled.filter((c) => c !== code && resolveModuleDependencies(c).includes(code));
}
/**
 * Modules that give a facility a reason to hold patient records.
 *
 * A standalone medical store sells over the counter — its POS already takes a
 * walk-in name and leaves the patient blank — so a full "one lifetime record
 * per person" registration, with an MRN, a blood group and a guardian, is not
 * something it ever needs. A laboratory, by contrast, cannot run a test without
 * knowing whose sample it is.
 */
export const PATIENT_BEARING_MODULES = [
    'opd',
    'ipd',
    'er',
    'ot',
    'lab',
    'radiology',
    'blood_bank',
    'maternity',
    'dialysis',
    'daycare',
    'dental',
    'mrd',
];
export const DOCTOR_BEARING_MODULES = [
    'opd',
    'ipd',
    'er',
    'ot',
    'maternity',
    'dialysis',
    'daycare',
    'dental',
    'radiology',
];
export const ENCOUNTER_MODULES = [
    'opd',
    'ipd',
    'er',
    'ot',
    'maternity',
    'dialysis',
    'daycare',
    'dental',
];
export const ADD_ON_MODULES = ['nphies', 'wasfaty'];
export function isAddOnModule(code) {
    return ADD_ON_MODULES.includes(code);
}
export function withoutAddOns(codes) {
    return codes.filter((code) => !isAddOnModule(code));
}
//# sourceMappingURL=modules.js.map